/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { useIntl } from 'gatsby-plugin-intl';

import RichText from '../RichText';
import * as Link from '../Link';
import Text, * as Texts from '../Text';
import Svg from '../Svg';
import leftChevron from '../../images/left-chevron-black.svg';
import rightChevron from '../../images/right-chevron-black.svg';
import useKeyValuePairs from '../../hooks/useKeyValuePairs';

import {
  ProductStepCard,
  Content,
  StepControls,
  StepButton,
  Panel,
  TitleWrapper,
  StepTitle,
  Body,
  Pagination,
  PageControl,
} from './styles';

const introTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => <Texts.Card>{children}</Texts.Card>,
    [INLINES.HYPERLINK]: (node: any, children: any) => (
      <Link.Primary href={node.data.uri} fontSize="inherit">
        {children}
      </Link.Primary>
    ),
  },
};

const phoneNumberOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => (
      <Text as="span" lineHeight={{ default: '28px', lg: '0px' }}>
        {children}
      </Text>
    ),
    [INLINES.HYPERLINK]: (node: any, children: any) => (
      <Link.Primary href={node.data.uri} fontSize="20px">
        {children}
      </Link.Primary>
    ),
  },
};

const MobileSteps = ({ data }: any) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { locale } = useIntl();
  const keyValuePairs = useKeyValuePairs(locale);

  if (!data) return null;
  const { tabItems } = data;

  const handleStepChange = (change: number) => {
    const newStep = currentStep + change;
    if (newStep > tabItems.length - 1 || newStep < 0) return null;
    setCurrentStep(newStep);
    return newStep;
  };

  const handleKeyPress = ({ key }: { key: string}) => {
    switch (key) {
      case 'ArrowRight':
        handleStepChange(1);
        break;
      case 'ArrowLeft':
        handleStepChange(-1);
        break;
      default:
    }
  };

  interface TabItem {
    id: string;
    tabTitle: string;
    heading: string;
    optionalSecondHeadingRTF: any;
    bodyRTF: any;
  }

  return (
    <ProductStepCard>
      <Content>
        <StepControls role="tablist">
          {tabItems.map(({ id, tabTitle }: TabItem, index: number) => (
            <StepButton
              key={id}
              onClick={() => setCurrentStep(index)}
              isActive={currentStep === index}
              aria-selected={currentStep === index}
              role="tab"
            >
              {tabTitle}
            </StepButton>
          ))}
        </StepControls>
        <Panel role="tabpanel" tabIndex={0} onKeyDown={handleKeyPress}>
          <TitleWrapper>
            <StepTitle>
              {tabItems[currentStep]?.heading}
              {' '}
            </StepTitle>
            {tabItems[currentStep]?.optionalSecondHeadingRTF && (
              <RichText
                document={tabItems[currentStep]?.optionalSecondHeadingRTF}
                options={phoneNumberOptions}
              />
            )}
          </TitleWrapper>
          <Body>
            <RichText
              document={tabItems[currentStep]?.bodyRTF}
              options={introTextOptions}
            />
          </Body>
        </Panel>
      </Content>
      <Pagination>
        <PageControl
          type="button"
          onClick={() => handleStepChange(-1)}
          disabled={currentStep <= 0}
          aria-label={keyValuePairs.STEPPER_PREVIOUS}
        >
          <Svg src={leftChevron} alt="" />
        </PageControl>
        <PageControl
          type="button"
          onClick={() => handleStepChange(1)}
          disabled={currentStep >= tabItems.length - 1}
          aria-label={keyValuePairs.STEPPER_NEXT}
        >
          <Svg src={rightChevron} alt="" />
        </PageControl>
      </Pagination>
    </ProductStepCard>
  );
};

export default MobileSteps;
